<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="salespersonTypes"
      :headers="salespersonTypesHeaders"
      :list="salespersonTypes"
      @add-new="onAddNewSalespersonType"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="missed-visits-reasons"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as SalesPersonType)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <SalespersonTypesModalCreateEdit
      v-if="createEditModalActive"
      :salesperson-type="salespersonType"
      :salesperson-types="salespersonTypes"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave"></SalespersonTypesModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getSalesPersonTypes, removeSalesPersonTypesByIds } from '@/services/api/user';
import { filterActive, filterData, sortData } from '@/utils/table';
import SalespersonTypesModalCreateEdit from '@/components/salespersonsManagement/SalespersonTypesModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { SalesPersonType } from '@/types/SalesPersons';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const { reloadLookups } = useLookupsStore();

const salespersonTypes = ref<SalesPersonType[]>([]);
const filterIsActive = ref<boolean | undefined>();
const salespersonTypesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const salespersonType = ref<SalesPersonType>(initializeSalespersonType());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeSalespersonType(): SalesPersonType {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewSalespersonType() {
  salespersonType.value = initializeSalespersonType();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onItemClick(item: SalesPersonType) {
  salespersonType.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: SalesPersonType) {
  if (!salespersonTypes.value) return;

  const index = salespersonTypes.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) salespersonTypes.value[index] = modifiedData;
  else salespersonTypes.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['salesPersonTypes']);
}

async function loadData(data?: TableFilterData) {
  let response = await getSalesPersonTypes();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, salespersonTypesHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  salespersonTypes.value = response;
}

async function onDeleteMultiselect(items: SalesPersonType[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (idsToDelete.length === 0) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeSalesPersonTypesByIds(idsToDelete);
  if (!res && salespersonTypes.value)
    salespersonTypes.value = salespersonTypes.value.filter(
      (type) => !idsToDelete.includes(type.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['salesPersonTypes']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeSalesPersonTypesByIds([idToDelete]);
  if (!res && salespersonTypes.value)
    salespersonTypes.value = salespersonTypes.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['salesPersonTypes']);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<SalesPersonType>();
function onDeleteRow(item: SalesPersonType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (salespersonType.value && !salespersonType.value.isFromManager)
    return `${$t('common.actions.view')} ${$t('salesperson.salespersonType')} `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('salesperson.salespersonType')}`
    : `${$t('common.actions.create')} ${$t('salesperson.salespersonType')}`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
